import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem('ea33_chat_session');
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem('ea33_chat_session', id);
  }
  return id;
};

const stripLead = (text) => text.split('[[')[0].trimEnd();

const renderText = (text) => {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const link = p.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (link) return <a key={i} href={link[2]} className="font-bold underline underline-offset-2 text-brand-gold-dark">{link[1]}</a>;
    const bold = p.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    return <span key={i}>{p}</span>;
  });
};

const QUICK_PROMPTS = ['How much does a garage floor cost?', '¿Qué ciudades cubren?', 'Do you resurface pool decks?'];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('ea33_chat_msgs')) || []; } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem('ea33_chat_msgs', JSON.stringify(messages.slice(-40)));
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', text: msg }, { role: 'bot', text: '' }]);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 90000);
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error('bad response');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();
        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.t) {
              acc += data.t;
              const display = stripLead(acc);
              setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: 'bot', text: display }; return c; });
            }
          } catch { /* partial json */ }
        }
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: 'bot', text: 'Sorry, something went wrong. Call us at (239) 276-1462 or use the quote form.' }; return c; });
    }
    clearTimeout(timer);
    setBusy(false);
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 flex w-[92vw] max-w-[380px] flex-col overflow-hidden rounded-2xl border-2 border-brand-gold/40 bg-white shadow-2xl" style={{ height: 'min(560px, 72vh)' }} data-testid="chat-widget-panel">
          <div className="flex items-center justify-between bg-brand-black px-4 py-3">
            <div>
              <p className="font-bold text-brand-gold">EpoxyArt33 Assistant</p>
              <p className="text-xs text-gray-400">AI · replies in English o español</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white" aria-label="Close chat" data-testid="chat-close-btn"><X className="h-5 w-5" /></button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-gray-50 p-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white border border-gray-200 px-4 py-3 text-sm text-gray-800 shadow-sm">
                  Hi! 👋 Ask me anything about epoxy floors, pool decks, prices or service areas. / ¡Hola! Pregúntame lo que quieras sobre pisos epóxicos, precios o ciudades.
                </div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PROMPTS.map((q, i) => (
                    <button key={q} onClick={() => send(q)} className="rounded-full border border-brand-gold/50 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-brand-gold/10 transition-colors" data-testid={`chat-quick-${i}`}>{q}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm shadow-sm ${m.role === 'user' ? 'rounded-tr-sm bg-brand-gold text-brand-black font-medium' : 'rounded-tl-sm bg-white border border-gray-200 text-gray-800'}`}
                  data-testid={m.role === 'user' ? 'chat-message-user' : 'chat-message-bot'}
                >
                  {m.text ? renderText(m.text) : <span className="inline-flex gap-1"><span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" /><span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '120ms' }} /><span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '240ms' }} /></span>}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-gray-200 bg-white p-3">
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder="Type your question… / Escribe aquí…"
                className="flex-1 rounded-full border border-gray-300 px-4 py-2.5 text-sm outline-none focus:border-brand-gold"
                data-testid="chat-input"
              />
              <button onClick={() => send()} disabled={busy} className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-gold text-brand-black transition-transform hover:scale-105 active:scale-95 disabled:opacity-50" aria-label="Send message" data-testid="chat-send-btn">
                <Send className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-center gap-4 text-[11px] text-gray-500">
              <a href="https://wa.me/12392761462?text=Hi!%20I%20am%20interested%20in%20your%20epoxy%20flooring%20services." target="_blank" rel="noopener noreferrer" className="font-semibold text-green-700 hover:underline" data-testid="chat-whatsapp-link">WhatsApp</a>
              <span>·</span>
              <a href="tel:2392761462" className="inline-flex items-center gap-1 font-semibold hover:underline" data-testid="chat-phone-link"><Phone className="h-3 w-3" /> (239) 276-1462</a>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="group fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-brand-gold to-brand-gold-dark shadow-2xl transition-transform duration-300 hover:scale-110"
        aria-label="Chat with us"
        data-testid="chat-widget-bubble"
      >
        {!open && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-gold opacity-60" />}
        {open ? <X className="relative h-7 w-7 text-brand-black" /> : <MessageCircle className="relative h-8 w-8 text-brand-black" fill="currentColor" />}
      </button>
    </>
  );
};

export default ChatWidget;
